import {useCallback, useEffect} from "react";
import {batch, useComputed, useSignal} from "@preact/signals-react";
import {useAppToast} from "@ui-seeker/app";
import {postSubmit, isEmpty, scrollToComponent, stringToDate, formatDate, FORMAT_DATE_BACK} from "@ui-seeker/utils";

import {VIEW_DATA} from "../@constants";
import { useMarketAnalyzerRoleConfig } from "../../../@hooks";
import { marketAnalyzerEvolutionSkuFilters } from "../../../@signals";


export default function usePriceDiscountEvolutionData() {
	const { showToast } = useAppToast()
	const { urlApiTable } = useMarketAnalyzerRoleConfig()

	const isLoading = useSignal(false)
	const averageMarketData = useSignal({})
	const skusSelected = useSignal([])
	const topRetailersData = useSignal({})
	const fiveRetailers = useSignal({})
	const currentSku = useSignal(null)
	const viewData = useSignal(VIEW_DATA.TOP_RETAILERS)
	const currency = useSignal('')
    const dateRange = useSignal([])

	const currentSkuSelected = useComputed(() => skusSelected.value.filter(item => item.skuID === currentSku.value)[0] || {})
	const isViewTopRetailersData = useComputed(() => viewData.value === VIEW_DATA.TOP_RETAILERS)
	const data = useComputed(() =>
		isViewTopRetailersData.value
			? topRetailersData.value[currentSku.value] || []
			: averageMarketData.value[currentSku.value] || []
	)

	useEffect(() => {
		if (!isEmpty(marketAnalyzerEvolutionSkuFilters.value)) batch(() => {
			isLoading.value = true
			averageMarketData.value = {}
		 	skusSelected.value  = []
		 	topRetailersData.value  = {}
		 	fiveRetailers.value  = {}
		 	currentSku.value  = null
			showToast.promise(postSubmit(`${urlApiTable}/evolution`, marketAnalyzerEvolutionSkuFilters.value).then(res => batch(() => {
				if (Object.keys(res.averageMarket).length > 0) {
					averageMarketData.value = res.averageMarket
					skusSelected.value = res.skusSelected
					topRetailersData.value = res.topRetailers
					fiveRetailers.value = res.fiveRetailers
					currentSku.value = (res.skusSelected[0] || {}).skuID
					currency.value = res.currencyChart
                    dateRange.value = [stringToDate(marketAnalyzerEvolutionSkuFilters.value.startDate), stringToDate(marketAnalyzerEvolutionSkuFilters.value.endDate)]
					isLoading.value = false
				} else {
					showToast.warning('Evolution Info not found')
				}
			})).catch(() => {
				isLoading.value = false
				showToast.error()
			})
			.finally(() => scrollToComponent('evolution-sku-chart')))
		}) 

		if (isEmpty(marketAnalyzerEvolutionSkuFilters.value)) batch( () => {
			averageMarketData.value = {}
		 	skusSelected.value  = []
		 	topRetailersData.value  = {}
		 	fiveRetailers.value  = {}
		 	currentSku.value  = null
		})

	}, [JSON.stringify(marketAnalyzerEvolutionSkuFilters.value)]);

    const onChange = (e) => {
        batch(() => {
            dateRange.value = e
            marketAnalyzerEvolutionSkuFilters.value = {
                ...marketAnalyzerEvolutionSkuFilters.value,
                startDate: formatDate(e[0], { format: FORMAT_DATE_BACK }),
                endDate: formatDate(e[1], { format: FORMAT_DATE_BACK }),
            }
        })
    }

	const resetEvolutionSkuData = () => {
		averageMarketData.value = {}
		skusSelected.value  = []
		topRetailersData.value  = {}
		fiveRetailers.value  = {}
		currentSku.value  = null
		marketAnalyzerEvolutionSkuFilters.value = {}
	}

	const handleViewData = useCallback(newValue => {
		viewData.value = newValue
	}, [])

	return {
		isLoading: isLoading.value,
		skusSelected: skusSelected.value,
		currentFiveRetailers: fiveRetailers.value[currentSku.value] || [],
		currentSku: currentSku.value,

		currentSkuSelected: currentSkuSelected.value,
		data: data.value,
		viewData: viewData.value,
		isViewTopRetailersData: isViewTopRetailersData.value,

		currency: currency.value,
        dateRange: dateRange.value,

        onChange, handleViewData, resetEvolutionSkuData
	}
}